<template>
  <section class="scene">

    <transition name="fade">
      <loader v-if="showLoaderInitial"
              :progress="loadingProgress"
              @close="showLoaderInitial = false"></loader>
    </transition>

    <transition name="fade">
      <scene-instructions v-if="instructions.show"
                          @close="onInstructionsClose"></scene-instructions>
    </transition>

    <transition name="fade">
      <div class="action instructions-toggle" v-if="isLoading"
           @click="instructions.show = !instructions.show"
           @mouseover="tooltipOptions = { content: 'Show introduction', styles: { top: '78px', right: '-14px' } }"
           @mouseleave="tooltipReset()">
        <svg class="icon">
          <use xlink:href="#helper-info"></use>
        </svg>
      </div>
    </transition>

    <div class="content">
      <div ref="sceneViewportWrap"
           class="scene-viewport-wrap"
           @sceneIsLoaded="isLoading = false"
           @sceneLoadStart="isLoading = true"
           @loadingProgress="loadingProgressChange"
           @movement="onMovement"
           @interactiveObjectClick="onInteractiveObjectClick"
           @interactiveObjectHover="onInteractiveObjectHover"
           @interactiveObjectLeave="onInteractiveObjectLeave"></div>
    </div>

    <scene-helpers :options="{ instructions: this.instructions, autoRotate: this.autoRotate }"
                   @introductionsShow="onInstructionsShow"
                   @toggleHighlight="onToggleHighlight"
                   @toggleDayNight="onToggleDayNight"
                   @toggleAutoRotate="onToggleAutoRotateEnabled"/>

    <scene-embedded-view ref="sceneEmbeddedView"
                         :options="{
                           config: embeddedView.config,
                           show: embeddedView.show,
                           backgroundMode: embeddedView.backgroundMode,
                           fullPageMode: embeddedView.fullPageMode,
                           frameMode: embeddedView.frameMode,
                           leaveAnimation: embeddedView.leaveAnimation
                         }"
                         @windowClose="onEmbeddedViewClose"/>

    <tooltip v-if="tooltipOptions.content" :options="tooltipOptions"/>

  </section>
</template>

<script>
  import Scene3D           from '../../scenes/Scene3D'
  import SceneInstructions from './Instructions'
  import SceneHelpers      from './Helpers'
  import SceneEmbeddedView from './embedded/View'
  import Loader            from '../LoaderInitial'
  import Tooltip           from '../common/Tooltip'

  export default {
    name: 'SceneView',

    components: { Tooltip, SceneInstructions, SceneHelpers, SceneEmbeddedView, Loader },

    props: ['options'],

    data() {
      return {
        isLoading: true,
        scenes: [],
        currentScene: null,
        embeddedView: {
          config: null,
          show: false,
          fullPageMode: false,
          frameMode: false,
          backgroundMode: false,
          leaveAnimation: null
        },
        instructions: {
          show: !this.isWelcomeShowed
        },
        autoRotate: {
          enabled: false,
          paused: true
        },
        highlightInteractiveObjects: false,
        tooltipOptions: {
          styles: {},
          content: ''
        },
        loadingProgress: {
          loaded: 0,
          total: 0
        },
        showLoaderInitial: true,
        movementEventTimer: null
      }
    },

    created() {
      this.instructions.show = /*this.isWelcomeShowed ? window.Config.scenes.instructionsShow : */true

      if (this.instructions.show) {
        this.onInstructionsShow()
      }

      this.initScene()
    },

    computed: {
      isWelcomeShowed() {
        let localConfig = this.getLocalConfig()
        return localConfig && localConfig.hasOwnProperty('welcomeShowed') && localConfig.welcomeShowed
      }
    },

    beforeDestroy() {
      if (this.currentScene) {
        this.currentScene.destroy()
        this.currentScene = null
      }
    },

    methods: {
      initScene() {
        this.$nextTick(() => {
          if (this.$refs.sceneViewportWrap) {

            if (this.currentScene) {
              this.currentScene.destroy()
            }

            let scene = new Scene3D(this.$refs.sceneViewportWrap)
            scene.setSceneConfig(window.Config.scenes.main)
            scene.setEmbeddedViewOptions(this.embeddedView)
            scene.init()
            scene.setCameraPosition(this.$route.query)
            scene.toggleAutoRotateEnabled(this.autoRotate.enabled)

            this.currentScene = scene
          }
        })
      },

      getLocalConfig() {
        let localConfig = window.localStorage.getItem('lightcolab')
        return localConfig ? JSON.parse(localConfig) : {}
      },

      loadingProgressChange(data) {
        this.loadingProgress = 0

        if (data.detail && data.detail.hasOwnProperty('loaded') && data.detail.hasOwnProperty('total')) {
          this.loadingProgress = data.detail
        }
      },

      onMovement(event) {
        if (this.movementEventTimer) {
          clearTimeout(this.movementEventTimer)
          this.movementEventTimer = null
        }

        this.movementEventTimer = setTimeout(() => {
          let query = Object.assign({}, this.$route.query, {
            x: event.detail.data.x.toFixed(1),
            y: event.detail.data.y.toFixed(1),
            lat: event.detail.data.lat.toFixed(1),
            lon: event.detail.data.lon.toFixed(1)
          })

          this.$router.replace({ path: this.$route.path, query: query })
        }, 500)
      },

      onInstructionsShow() {
        this.instructionsToggle(true)
      },

      onInstructionsClose() {
        this.instructionsToggle(false)
        let localConfig = this.getLocalConfig()
        localConfig.welcomeShowed = true
        window.localStorage.setItem('lightcolab', JSON.stringify(localConfig))
      },

      initEmbeddedView(embeddedSceneConfig) {
        this.embeddedView.show = true
        this.embeddedView.config = embeddedSceneConfig
        this.autoRotate.paused = true
        this.currentScene.toggleAutoRotatePause(true)
      },

      onEmbeddedViewClose() {
        this.embeddedView.show = false
        this.embeddedView.config = null
        this.autoRotate.paused = false
        this.currentScene.toggleAutoRotatePause(false, true)
      },

      instructionsToggle(value) {
        this.instructions.show = value
        this.autoRotate.paused = value

        if (this.currentScene) {
          this.currentScene.toggleAutoRotatePause(value)
        }
      },

      onToggleDayNight(value) {
        this.currentScene.switchEnvironmentScheme(value ? 'day' : 'lateEvening')
      },

      onToggleHighlight() {
        this.highlightInteractiveObjects = !this.highlightInteractiveObjects
        this.currentScene.setHighlightInteractiveObjects(this.highlightInteractiveObjects)
      },

      onToggleAutoRotateEnabled() {
        this.autoRotate.enabled = !this.autoRotate.enabled
        this.autoRotate.paused = !this.autoRotate.enabled
        this.currentScene.toggleAutoRotateEnabled(this.autoRotate.enabled)
      },

      onInteractiveObjectClick(event) {
        if (event.detail.embedded) {
          let embeddedSceneConfig = event.detail.embedded

          if (typeof this.currentScene.animationOnEmbeddedOpen === 'function') {
            this.initEmbeddedView(embeddedSceneConfig)
            this.currentScene.animationOnEmbeddedOpen(embeddedSceneConfig)
          } else {
            // just init
            this.initEmbeddedView(embeddedSceneConfig)
          }

          if (typeof this.currentScene.animationOnEmbeddedOpen === 'function') {
            this.embeddedView.leaveAnimation = this.currentScene.animationOnEmbeddedLeave.bind(this.currentScene)
          } else {
            this.embeddedView.leaveAnimation = null
          }
        }
      },

      onInteractiveObjectHover(event) {
        this.tooltipOptions = {
          content: event.detail.data.title,
          styles: {
            top: event.detail.mousePosition[1] * 100 + '%',
            left: event.detail.mousePosition[0] * 100 + '%',
            margin: '40px auto',
            backgroundColor: '#ccc',
            color: '#000'
          }
        }
      },

      onInteractiveObjectLeave(event) {
        this.tooltipReset()
      },

      tooltipReset() {
        this.tooltipOptions = { content: '', styles: {} }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "../../assets/styles/main";

  .welcome-image {
    @include border-radius(4px);
    background-size: cover;
    width: 100%;
    height: 300px;
    max-height: 300px;
  }

  .scene-viewport-wrap {
    width: 100%;
    height: 100vh;

    canvas {
      width: 100%;
      height: 100vh;
    }
  }

  .content {
    position: relative;
  }

  .action {
    &.instructions-toggle {
      border: 2px solid #ffffff;
      @include border-radius(50%);
      color: #ffffff;
      cursor: pointer;
      padding: 8px 10px;
      width: 40px;
      height: 40px;
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 2;

      .icon {
        width: 100%;
        height: 100%;
      }
    }
  }
</style>
